import { queryClient } from "@/api/client"
import type { Customization } from "@/api/customization"
import { ProductType } from "@/api/shared"
import type { DenormalizedTemplate } from "@/api/templates"
import type { DenormalizedWorkspace } from "@/api/workspaces"
import { queryKeys } from "@/queries/keys"
import { toWorkspace } from "@/queries/workspaces"
import type { ViewData } from "@/util/viewManager"

export function hydrateQueriesFromViewData(viewData: ViewData) {
  queryClient.removeQueries({ queryKey: queryKeys.customizations.all })
  queryClient.removeQueries({ queryKey: queryKeys.environments.all })
  queryClient.removeQueries({ queryKey: queryKeys.importerWebhooks.all })
  queryClient.removeQueries({ queryKey: ["templates"] })
  queryClient.removeQueries({ queryKey: queryKeys.workspaces.all })

  if (viewData.customization) {
    const customization = viewData.customization as Customization
    queryClient.setQueryData(queryKeys.customizations.default, customization)
    queryClient.setQueryData(queryKeys.customizations.all, [customization])
  }

  if (viewData.webhooks) {
    queryClient.setQueryData(queryKeys.importerWebhooks.all, viewData.webhooks)
  }

  const environments = viewData.environments
  if (Array.isArray(environments)) {
    queryClient.setQueryData(queryKeys.environments.all, environments)
  }

  const workspaces = viewData.workspaces
  if (Array.isArray(workspaces)) {
    // Store the normalized (`listIds`, no nested `lists`) shape the consumer
    // hooks read. The `lists` Redux slice is seeded separately from the same
    // viewData by `initializeStoreFromViewData`.
    queryClient.setQueryData(
      queryKeys.workspaces.all,
      workspaces.filter(isDenormalizedWorkspace).map(toWorkspace),
    )
  }

  const templates = viewData.templates
  if (Array.isArray(templates)) {
    const denormalized = templates.filter(isDenormalizedTemplate)
    queryClient.setQueryData(queryKeys.templates.all, denormalized)
    for (const template of denormalized) {
      queryClient.setQueryData(queryKeys.templates.byId(template.id), template)
    }
    // View data templates are intentionally incomplete (missing picklistOptions
    // for performance). Invalidate the per-id detail entries so useTemplateById
    // triggers a background refetch of the full template from the API while
    // rendering instantly from the hydrated placeholder data. The `all` list
    // cache is left untouched — list consumers render from the incomplete data
    // and must not trigger a refetch.
    void queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === "templates" && query.queryKey[1] !== "all",
    })
  }
}

function isDenormalizedWorkspace(value: unknown): value is DenormalizedWorkspace {
  if (typeof value !== "object" || value === null) {
    return false
  }

  const workspace = value as Partial<DenormalizedWorkspace>
  return (
    typeof workspace.id === "number" &&
    workspace.productType !== undefined &&
    Object.values(ProductType).includes(workspace.productType)
  )
}

function isDenormalizedTemplate(value: unknown): value is DenormalizedTemplate {
  if (typeof value !== "object" || value === null) {
    return false
  }

  return typeof (value as Partial<DenormalizedTemplate>).id === "number"
}
